import { Controller } from "@hotwired/stimulus"
import { Calendar } from '@fullcalendar/core'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import { DatepickerManager } from '../lib/datepicker_manager'

export default class extends Controller {
  connect() {
    // Get the already-normalized IANA timezone from the server
    this.userTimezone = document.querySelector('meta[name="user-timezone"]').getAttribute('content') || 'UTC'
    this.eventsUrl = document.querySelector('meta[name="calendar-events-url"]').getAttribute('content')
    this.createUrl = document.querySelector('meta[name="calendar-create-url"]').getAttribute('content')
    this.userDateFormat = document.querySelector('meta[name="user-date-format"]')?.getAttribute('content') || 'mm/dd/yyyy'
    
    // Store bound event handlers for cleanup
    this.boundHandleResize = this.handleResize.bind(this)
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    this.boundHandleFormSubmit = this.handleFormSubmit.bind(this)
    
    this.initializeCalendar()
    this.initializeModal()
    this.initializeQuickAdd()
  }
  
  disconnect() {
    // Clean up event listeners
    if (this.boundHandleResize) {
      window.removeEventListener('resize', this.boundHandleResize)
    }

    if (this.form) {
      if (this.boundHandleKeydown) {
        this.form.removeEventListener('keydown', this.boundHandleKeydown)
      }
      if (this.boundHandleFormSubmit) {
        this.form.removeEventListener('submit', this.boundHandleFormSubmit)
      }
    }
    
    // Destroy calendar instance
    if (this.calendar) {
      this.calendar.destroy()
    }
    
    // Clean up any datepickers
    this.cleanupDatepickers()
  }
  
  handleResize() {
    if (this.calendar) {
      this.calendar.updateSize()
    }
  }
  
  handleKeydown(e) {
    if (e.ctrlKey && e.key === 'Enter') {
      e.preventDefault()
      this.handleFormSubmit(e)
    }
  }
  
  cleanupDatepickers() {
    // Clean up all datepickers in the modal
    document.querySelectorAll('#eventModal [data-datepicker]').forEach(element => {
      if (element._datepicker) {
        DatepickerManager.destroy(element)
      }
    })
  }

  initializeCalendar() {
    
    this.calendar = new Calendar(this.element, {
      plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
      initialView: 'timeGridWeek',
      headerToolbar: {
        left: 'prev,next today',
        center: 'title',
        right: 'dayGridMonth,timeGridWeek,timeGridDay'
      },
      height: 'auto', // Let calendar determine optimal height
      contentHeight: 'auto', // Prevent stretching of time slots
      // Use the IANA timezone directly from the server
      timeZone: this.userTimezone,
      editable: true,
      selectable: true,
      selectMirror: true,
      weekends: true,
      nowIndicator: true,
      slotMinTime: '00:00:00', // Start at midnight
      slotMaxTime: '24:00:00', // End at midnight (full 24 hours)
      slotDuration: '00:30:00', // Keep 30-minute slots
      snapDuration: '00:15:00', // Still snap to 15 minutes for precision
      allDaySlot: false,
      slotLabelInterval: '01:00:00', // Show hour labels every hour
      scrollTime: '06:00:00', // Default scroll position
      
      events: this.eventsUrl,
      
      select: this.handleSelect.bind(this),
      eventClick: this.handleEventClick.bind(this),
      eventDrop: this.handleEventDrop.bind(this),
      eventResize: this.handleEventResize.bind(this),
      
      // Scroll to current time when switching views
      viewDidMount: (arg) => {
        // Only scroll for time grid views
        if (arg.view.type.includes('timeGrid')) {
          requestAnimationFrame(() => {
            this.scrollToCurrentTime()
          })
        }
      },
      
      eventContent: function(info) {
        const duration = info.event.extendedProps.duration
        return {
          html: `
            <div class="fc-event-content p-1">
              <div class="fc-event-title font-medium text-xs">${info.event.title}</div>
              <div class="fc-event-duration text-xs opacity-75">${duration}</div>
            </div>
          `
        }
      }
    })

    this.calendar.render()
    
    // Scroll to current time immediately after render
    requestAnimationFrame(() => {
      this.scrollToCurrentTime()
    })
    
    // Handle window resize to adjust calendar height
    window.addEventListener('resize', this.boundHandleResize)
  }

  initializeModal() {
    // Cache all DOM elements at once
    this.modal = document.getElementById('eventModal')
    this.form = document.getElementById('eventForm')
    this.deleteBtn = document.getElementById('deleteEventBtn')
    this.cancelBtn = document.getElementById('cancelEventBtn')
    this.closeBtn = document.getElementById('closeModalBtn')
    this.saveBtn = document.getElementById('saveEventBtn')
    
    // Cache form fields for later use
    this.categorySelect = document.getElementById('eventCategory')
    this.notesField = document.getElementById('eventNotes')
    this.startDateField = document.getElementById('eventStartDate')
    this.startTimeField = document.getElementById('eventStartTime')
    this.endDateField = document.getElementById('eventEndDate')
    this.endTimeField = document.getElementById('eventEndTime')
    
    // Delete confirmation modal elements
    this.deleteConfirmModal = document.getElementById('deleteConfirmModal')
    this.confirmDeleteBtn = document.getElementById('confirmDeleteBtn')
    this.cancelDeleteBtn = document.getElementById('cancelDeleteBtn')
    this.closeDeleteModalBtn = document.getElementById('closeDeleteModal')

    if (!this.modal) {
      return
    }

    if (this.form) {
      this.form.addEventListener('submit', this.boundHandleFormSubmit)
      
      // Add Ctrl+Enter shortcut to save using the bound handler
      this.form.addEventListener('keydown', this.boundHandleKeydown)
    }
    if (this.deleteBtn) this.deleteBtn.addEventListener('click', this.showDeleteConfirmModal.bind(this))
    if (this.cancelBtn) this.cancelBtn.addEventListener('click', this.hideModal.bind(this))
    if (this.closeBtn) this.closeBtn.addEventListener('click', this.hideModal.bind(this))
    
    // Delete confirmation modal listeners
    if (this.confirmDeleteBtn) this.confirmDeleteBtn.addEventListener('click', this.handleDelete.bind(this))
    if (this.cancelDeleteBtn) this.cancelDeleteBtn.addEventListener('click', this.hideDeleteConfirmModal.bind(this))
    if (this.closeDeleteModalBtn) this.closeDeleteModalBtn.addEventListener('click', this.hideDeleteConfirmModal.bind(this))
    
    // Close delete modal when clicking outside
    if (this.deleteConfirmModal) {
      this.deleteConfirmModal.addEventListener('click', (e) => {
        if (e.target === this.deleteConfirmModal) {
          this.hideDeleteConfirmModal()
        }
      })
    }
    
    // Close modal when clicking outside
    this.modal.addEventListener('click', (e) => {
      // Close if clicking the overlay or outside the modal content
      if (e.target.classList.contains('fixed') && e.target.classList.contains('inset-0')) {
        this.hideModal()
      }
    })
  }

  initializeQuickAdd() {
    const quickAddBtn = document.getElementById('quickAddBtn')
    
    if (!quickAddBtn) {
      return
    }
    
    quickAddBtn.addEventListener('click', () => {
      const now = new Date()
      const start = new Date(now.getTime())
      start.setMinutes(Math.floor(start.getMinutes() / 15) * 15) // Round to nearest 15 minutes
      const end = new Date(start.getTime() + 60 * 60 * 1000) // Add 1 hour
      
      this.showModal({
        start: start,
        end: end
      })
    })
  }

  handleSelect(info) {
    // FullCalendar provides the dates in the calendar's timezone
    // We need to use the string representations to preserve the timezone
    
    this.showModal({
      start: info.start,
      end: info.end,
      startStr: info.startStr,
      endStr: info.endStr
    })
  }

  handleEventClick(info) {
    // For existing events, we can use the startStr and endStr if available
    this.showModal({
      event: info.event,
      start: info.event.start,
      end: info.event.end,
      startStr: info.event.startStr,
      endStr: info.event.endStr
    })
  }

  handleEventDrop(info) {
    this.updateEvent(info.event)
  }

  handleEventResize(info) {
    this.updateEvent(info.event)
  }

  showModal(data) {
    this.currentEvent = data.event || null
    
    // Check if modal elements exist
    if (!this.modal) {
      return
    }
    
    // Populate form using cached elements
    if (this.categorySelect) this.categorySelect.value = data.event?.extendedProps.categoryId || ''
    if (this.notesField) this.notesField.value = data.event?.extendedProps.notes || ''
    
    // Format and set start date/time
    if (this.startDateField && this.startTimeField) {
      // If we have timezone-aware strings from FullCalendar, use them
      if (data.startStr) {
        const { date, time } = this.parseISOString(data.startStr)
        this.startDateField.value = date
        this.startTimeField.value = time
      } else {
        // Fallback to Date object (for quick add, etc)
        const startDate = this.formatDate(data.start)
        const startTime = this.formatTime(data.start)
        this.startDateField.value = startDate
        this.startTimeField.value = startTime
      }
    }
    
    // Format and set end date/time
    if (this.endDateField && this.endTimeField) {
      // If we have timezone-aware strings from FullCalendar, use them
      if (data.endStr) {
        const { date, time } = this.parseISOString(data.endStr)
        this.endDateField.value = date
        this.endTimeField.value = time
      } else {
        // Fallback to Date object (for quick add, etc)
        const endDate = this.formatDate(data.end)
        const endTime = this.formatTime(data.end)
        this.endDateField.value = endDate
        this.endTimeField.value = endTime
      }
    }
    
    // Show/hide delete button based on whether editing or creating
    if (this.deleteBtn) {
      if (this.currentEvent) {
        this.deleteBtn.classList.remove('hidden')
      } else {
        this.deleteBtn.classList.add('hidden')
      }
    }
    
    // Update button text based on whether editing or creating
    if (this.saveBtn) {
      this.saveBtn.textContent = this.currentEvent ? 'Update Time Log' : 'Save Time Log'
    }
    
    this.modal.classList.remove('hidden')
    
    // Force a style recalculation
    this.modal.offsetHeight
    
    // Auto-focus the category field when modal opens
    setTimeout(() => {
      if (this.categorySelect) {
        this.categorySelect.focus()
      }
    }, 50)
    
    // Initialize datepickers after modal is shown
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      // Simply use the standard DatepickerManager like the time log form
      DatepickerManager.initializeDatepickers()
      
      // After initialization, update the datepickers with the pre-filled values
      setTimeout(() => {
        // If the fields have values and datepickers, update them
        if (this.startDateField?._datepicker && this.startDateField.value) {
          this.startDateField._datepicker.setDate(this.startDateField.value)
        }
        
        if (this.endDateField?._datepicker && this.endDateField.value) {
          this.endDateField._datepicker.setDate(this.endDateField.value)
        }
      }, 100)
    })
  }

  hideModal() {
    // Clean up datepickers properly
    document.querySelectorAll('#eventModal [data-datepicker]').forEach(element => {
      if (element._datepicker) {
        DatepickerManager.destroy(element)
      }
    })
    
    this.modal.classList.add('hidden')
    this.currentEvent = null
    this.form.reset()
  }

  async handleFormSubmit(e) {
    e.preventDefault()
    
    const formData = new FormData(this.form)
    
    // Combine date and time fields into datetime strings
    const startDate = formData.get('start_date')
    const startTime = formData.get('start_time')
    const endDate = formData.get('end_date')
    const endTime = formData.get('end_time')
    
    // Parse and format dates for submission
    const startDateTime = this.combineDateAndTime(startDate, startTime)
    const endDateTime = this.combineDateAndTime(endDate, endTime)
    
    const data = {
      category_id: formData.get('category_id'),
      notes: formData.get('notes'),
      start: startDateTime,
      end: endDateTime
    }


    try {
      let response
      if (this.currentEvent) {
        // Update existing event
        response = await fetch(`${this.createUrl}/${this.currentEvent.id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
          },
          body: JSON.stringify(data)
        })
      } else {
        // Create new event
        response = await fetch(this.createUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
          },
          body: JSON.stringify(data)
        })
      }

      if (response.ok) {
        this.calendar.refetchEvents()
        this.hideModal()
        this.showSuccessMessage(this.currentEvent ? 'Event updated!' : 'Event created!')
      } else {
        const errorData = await response.json()
        this.showErrorMessage(errorData.errors?.join(', ') || 'Something went wrong')
      }
    } catch (error) {
      this.showErrorMessage('Network error occurred')
    }
  }

  async updateEvent(event) {
    // Send the ISO string which includes timezone information
    // The server needs to know this is already in the correct timezone
    const data = {
      start: event.startStr || event.start.toISOString(),
      end: event.endStr || event.end.toISOString()
    }

    try {
      const response = await fetch(`${this.createUrl}/${event.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        },
        body: JSON.stringify(data)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ errors: ['Failed to update event'] }))
        event.revert()
        this.showErrorMessage(errorData.errors?.join(', ') || 'Failed to update event')
      }
    } catch (error) {
      console.error('Error updating event:', error)
      event.revert()
      this.showErrorMessage('Network error occurred: ' + error.message)
    }
  }

  showDeleteConfirmModal() {
    if (this.deleteConfirmModal) {
      this.deleteConfirmModal.classList.remove('hidden')
    }
  }

  hideDeleteConfirmModal() {
    if (this.deleteConfirmModal) {
      this.deleteConfirmModal.classList.add('hidden')
    }
  }

  async handleDelete() {
    if (!this.currentEvent) return

    try {
      const response = await fetch(`${this.createUrl}/${this.currentEvent.id}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        }
      })

      if (response.ok) {
        this.calendar.refetchEvents()
        this.hideDeleteConfirmModal()
        this.hideModal()
        this.showSuccessMessage('Time log deleted!')
      } else {
        this.showErrorMessage('Failed to delete time log')
      }
    } catch (error) {
      this.showErrorMessage('Network error occurred')
    }
  }

  parseISOString(isoString) {
    // Parse ISO string from FullCalendar (e.g., "2024-01-15T10:30:00+07:00")
    // Extract just the date and time parts, ignoring timezone since FullCalendar
    // already gives us the correct local time in the user's timezone
    
    // Handle both full ISO strings and date-only strings
    const parts = isoString.split('T')
    const datePart = parts[0]
    const timePart = parts[1] ? parts[1].substring(0, 5) : '00:00' // Get HH:MM
    
    // Parse the date part
    const [year, month, day] = datePart.split('-')
    
    // Format date according to user preference
    let formattedDate
    switch (this.userDateFormat) {
      case 'dd/mm/yyyy':
        formattedDate = `${day}/${month}/${year}`
        break
      case 'yyyy-mm-dd':
        formattedDate = datePart // Already in the right format
        break
      case 'mm/dd/yyyy':
      default:
        formattedDate = `${month}/${day}/${year}`
        break
    }
    
    return {
      date: formattedDate,
      time: timePart
    }
  }

  formatDate(date) {
    // Format date according to user preference
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const year = date.getFullYear()
    
    switch (this.userDateFormat) {
      case 'dd/mm/yyyy':
        return `${day}/${month}/${year}`
      case 'yyyy-mm-dd':
        return `${year}-${month}-${day}`
      case 'mm/dd/yyyy':
      default:
        return `${month}/${day}/${year}`
    }
  }
  
  formatTime(date) {
    // Format time for time input (HH:MM)
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    return `${hours}:${minutes}`
  }
  
  combineDateAndTime(dateStr, timeStr) {
    // Parse date based on user format and time (HH:MM) and combine into ISO datetime
    let month, day, year
    
    switch (this.userDateFormat) {
      case 'dd/mm/yyyy':
        [day, month, year] = dateStr.split('/')
        break
      case 'yyyy-mm-dd':
        [year, month, day] = dateStr.split('-')
        break
      case 'mm/dd/yyyy':
      default:
        [month, day, year] = dateStr.split('/')
        break
    }
    
    const [hours, minutes] = timeStr.split(':')
    
    // Create date object in local time
    const date = new Date(year, month - 1, day, hours, minutes)
    
    // Return ISO string for submission to server
    return date.toISOString()
  }
  
  formatDateTimeLocal(date) {
    // Legacy method kept for backward compatibility
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    return `${year}-${month}-${day}T${hours}:${minutes}`
  }

  showSuccessMessage() {
    // You can integrate with your existing flash message system
    // Message parameter removed since it's not being used
  }

  showErrorMessage(message) {
    // You can integrate with your existing flash message system
    alert(message) // Temporary - replace with your flash system
  }

  scrollToCurrentTime() {
    try {
      // Get current time in user's configured timezone
      const now = new Date()
      const userTimeString = now.toLocaleString("en-US", { timeZone: this.userTimezone })
      const userTime = new Date(userTimeString)
      // Scroll to 1 hour before current time to show more upcoming hours
      const scrollHour = Math.max(0, userTime.getHours() - 1)
      
      // Find the parent scrollable container with overflow-auto class
      const scrollContainer = this.element.closest('.overflow-auto')
      
      if (!scrollContainer) {
        console.debug('Scroll container not found')
        return
      }
      
      // Try multiple selectors for finding the time slot
      const targetTime = `${String(scrollHour).padStart(2, '0')}:00:00`
      let targetSlot = this.element.querySelector(`[data-time="${targetTime}"]`)
      
      // Fallback: try FullCalendar's time grid slots
      if (!targetSlot) {
        const fcSlots = this.element.querySelectorAll('.fc-timegrid-slot')
        for (const slot of fcSlots) {
          const slotTime = slot.getAttribute('data-time')
          if (slotTime && slotTime.startsWith(`${String(scrollHour).padStart(2, '0')}:`)) {
            targetSlot = slot
            break
          }
        }
      }
      
      // Another fallback: find by text content
      if (!targetSlot) {
        const timeLabels = this.element.querySelectorAll('.fc-timegrid-slot-label')
        for (const label of timeLabels) {
          if (label.textContent?.includes(`${scrollHour}`) || 
              label.textContent?.includes(`${scrollHour}am`) || 
              label.textContent?.includes(`${scrollHour}pm`)) {
            targetSlot = label.closest('.fc-timegrid-slot') || label
            break
          }
        }
      }
      
      if (targetSlot) {
        // Calculate the scroll position
        const scrollPosition = targetSlot.offsetTop - scrollContainer.offsetTop
        
        // Smooth scroll to the position
        scrollContainer.scrollTo({
          top: scrollPosition,
          behavior: 'smooth'
        })
      } else {
        console.debug(`Could not find time slot for ${targetTime}`)
      }
    } catch (error) {
      console.error('Error scrolling to current time:', error)
    }
  }
}